import React, { useEffect } from 'react';
import { Modal, Platform, Pressable, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, spacing, typography } from '../theme';
import { useRTLStyles } from '../hooks/useRTLStyles';
import { useIsMobileBrowser } from '../hooks/useIsMobileBrowser';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { InsetModal } from './InsetModal';

const isWeb = Platform.OS === 'web';

/**
 * The app's overlay look (global search, filters, pickers): a dimmed, lightly blurred backdrop and
 * a raised rounded card. Functions, not constants — colours follow the theme.
 */
export const overlayBackdropStyle = () => ({
  backgroundColor: 'rgba(5, 8, 18, 0.55)',
  ...(isWeb ? ({ backdropFilter: 'blur(3px)', WebkitBackdropFilter: 'blur(3px)' } as any) : {}),
});
/** The card's look without clipping — for cards that hold dropdowns or anchored menus. */
export const overlayCardLook = () => ({
  backgroundColor: colors.background.surfaceRaised,
  borderRadius: 16,
  borderWidth: 1,
  borderColor: colors.borderTokens.medium,
  ...(isWeb ? ({ boxShadow: '0 30px 80px -20px rgba(0,0,0,.55)' } as any) : {}),
});
export const overlayCardStyle = () => ({
  backgroundColor: colors.background.surfaceRaised,
  borderRadius: 16,
  borderWidth: 1,
  borderColor: colors.borderTokens.medium,
  overflow: 'hidden' as const,
  ...(isWeb ? ({ boxShadow: '0 30px 80px -20px rgba(0,0,0,.55)' } as any) : {}),
});

interface Props {
  visible: boolean;
  onClose: () => void;
  children: React.ReactNode;
  /** Card width on desktop. */
  maxWidth?: number;
  /**
   * Phones: 'page' = full screen, the content pads the safe area itself (filter pages);
   * 'inset' = full screen padded by InsetModal; 'center' = a centered card like desktop.
   */
  mobile?: 'page' | 'inset' | 'center';
  accessibilityLabel?: string;
}

/**
 * A panel over the current screen. Desktop web: a centered card over the dimmed, blurred page
 * (clicking outside closes it). Phones: a full-screen page or a centered card.
 */
export const OverlayPanel: React.FC<Props> = ({ visible, onClose, children, maxWidth = 640, mobile = 'page', accessibilityLabel }) => {
  const styles = useThemedStyles(getStyles);
  const isMobileBrowser = useIsMobileBrowser();
  const desktop = isWeb && !isMobileBrowser;
  useEscape(visible && desktop, onClose);

  if (desktop || mobile === 'center') {
    return (
      <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
        <Pressable style={[styles.backdrop, desktop ? styles.backdropDesktop : styles.backdropCenter]} onPress={onClose} accessibilityLabel={accessibilityLabel}>
          {/* Clicks inside the card stay inside. */}
          <Pressable onPress={(e) => e.stopPropagation?.()} style={[styles.card, { maxWidth }, desktop ? styles.cardDesktop : styles.cardCenter]} accessibilityViewIsModal>
            {children}
          </Pressable>
        </Pressable>
      </Modal>
    );
  }
  if (mobile === 'inset') {
    return <InsetModal visible={visible} animationType="slide" onRequestClose={onClose}>{children}</InsetModal>;
  }
  return <Modal visible={visible} transparent animationType="slide" onRequestClose={onClose}>{children}</Modal>;
};

const getStyles = () => StyleSheet.create({
  backdrop: { flex: 1, alignItems: 'center', paddingHorizontal: 16, ...overlayBackdropStyle() },
  backdropDesktop: { paddingTop: '9vh' as any, paddingBottom: '6vh' as any },
  backdropCenter: { justifyContent: 'center' },
  card: { width: '100%', ...overlayCardStyle() },
  // A column whose height follows its content up to the screen; the content's own ScrollView scrolls.
  cardDesktop: { maxHeight: '86vh' as any, display: 'flex' as any, flexDirection: 'column', cursor: 'auto' as any },
  cardCenter: { maxHeight: '85%' },
});

/** For screens that build their own overlay (small pickers): the same backdrop and card. */
export const overlayPickerStyles = () => ({
  overlay: { flex: 1, alignItems: 'center' as const, justifyContent: 'center' as const, padding: 16, ...overlayBackdropStyle() },
  card: { width: '100%' as const, maxWidth: 440, maxHeight: '80%' as const, padding: 16, gap: 8, ...overlayCardStyle() },
});

/** Esc closes the top overlay on desktop web. */
export function useEscape(active: boolean, onClose: () => void) {
  useEffect(() => {
    if (!active || !isWeb) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.preventDefault(); onClose(); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, onClose]);
}

/**
 * The overlay header, as in global search: a gold icon, the title from the start, extra actions,
 * and an "Esc" key on desktop (a back arrow on phones).
 */
export const OverlayHeader: React.FC<{ icon?: string; title: string; subtitle?: string; onClose: () => void; right?: React.ReactNode }> = ({ icon = 'options-outline', title, subtitle, onClose, right }) => {
  const styles = useThemedStyles(getHeaderStyles);
  const rtl = useRTLStyles();
  const isMobileBrowser = useIsMobileBrowser();
  const desktop = isWeb && !isMobileBrowser;
  return (
    <View style={[styles.header, { flexDirection: rtl.rowDirection }]}>
      {desktop ? (
        <View style={styles.iconBox}><Ionicons name={icon as any} size={18} color={colors.primary as string} /></View>
      ) : (
        <TouchableOpacity onPress={onClose} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }} accessibilityRole="button">
          <Ionicons name={rtl.isRTL ? 'arrow-forward' : 'arrow-back'} size={24} color={colors.text.primary as string} />
        </TouchableOpacity>
      )}
      <View style={styles.titleBox}>
        <Text style={[styles.title, { textAlign: rtl.textStart }]} numberOfLines={1}>{title}</Text>
        {!!subtitle && <Text style={[styles.subtitle, { textAlign: rtl.textStart }]} numberOfLines={1}>{subtitle}</Text>}
      </View>
      {right}
      {desktop && (
        <TouchableOpacity onPress={onClose} accessibilityRole="button" accessibilityLabel="Esc">
          <Text style={styles.kbd}>Esc</Text>
        </TouchableOpacity>
      )}
    </View>
  );
};

/** The overlay footer, as in global search: a quiet bar; on desktop the action sits at the end. */
export const OverlayFooter: React.FC<{ hint?: React.ReactNode; children: React.ReactNode }> = ({ hint, children }) => {
  const styles = useThemedStyles(getHeaderStyles);
  const rtl = useRTLStyles();
  const isMobileBrowser = useIsMobileBrowser();
  const desktop = isWeb && !isMobileBrowser;
  if (!desktop) return <View style={styles.footerMobile}>{children}</View>;
  return (
    <View style={[styles.footer, { flexDirection: rtl.rowDirection }]}>
      <View style={styles.footerHint}>{typeof hint === 'string' ? <Text style={[styles.hintText, { textAlign: rtl.textStart }]}>{hint}</Text> : hint}</View>
      <View style={styles.footerAction}>{children}</View>
    </View>
  );
};

const getHeaderStyles = () => StyleSheet.create({
  header: { flexShrink: 0, alignItems: 'center', gap: spacing.sm, paddingHorizontal: spacing.md, paddingVertical: spacing.sm + 4, borderBottomWidth: 1, borderBottomColor: colors.border, backgroundColor: colors.background.surfaceRaised },
  iconBox: { width: 34, height: 34, borderRadius: 9, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.primaryTransparent },
  titleBox: { flex: 1, minWidth: 0 },
  title: { fontSize: typography.fontSize.lg, fontWeight: '700', color: colors.text.primary },
  subtitle: { fontSize: typography.fontSize.xs, color: colors.text.secondary, marginTop: 1 },
  kbd: { fontSize: 11, color: colors.text.secondary, borderWidth: 1, borderColor: colors.border, borderRadius: 6, paddingHorizontal: 6, paddingVertical: 2, marginStart: spacing.xs },
  footer: { flexShrink: 0, alignItems: 'center', gap: spacing.md, paddingHorizontal: spacing.md, paddingVertical: 10, borderTopWidth: 1, borderTopColor: colors.border, backgroundColor: colors.background.surface },
  footerHint: { flex: 1, minWidth: 0 },
  hintText: { fontSize: 12, color: colors.text.secondary },
  footerAction: { minWidth: 200 },
  footerMobile: { flexShrink: 0, padding: spacing.lg, borderTopWidth: 1, borderTopColor: colors.border, backgroundColor: colors.background.surface },
});
